import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { imageSetAvatarThunk } from "../image/imageSetAvatar";
import { ObjectTarget } from "../../app/permissions";

export interface RoutineState {
    state: LoadStateInfo;
    request?: RetrieveRoutineRequest;
    selected?: RoutineInfo;
}

const initialState: RoutineState = {
    state: notStarted(),
};


export interface RetrieveRoutineRequest {
    routineId: string;
}

export const retrieveRoutine = createAsyncThunk(
    "retrieveRoutine",
    async (param: RetrieveRoutineRequest, thunkAPI) => {
        console.debug("retrieve routine");

        const response = await fetch(`${getBackendUrl()}/api/routine/${param.routineId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveRoutineRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.routine;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveRoutine skipped: ${status.state.state}`);
            return false;
        }
    }
);


export const routineSlice = createSlice({
    name: "routine",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        select: (state, action: PayloadAction<RoutineInfo | undefined>) => {
            console.trace(`select :: ${action.payload?.name}`);
            state.selected = action.payload;
        },
        check: (state, action: PayloadAction<string | undefined>) => {
            if (state.state.state != LoadState.NotStarted
                && (action.payload == undefined || state.request?.routineId != action.payload)
            ) {
                state.request = undefined;
                state.state = notStarted();
                state.selected = undefined;
            }
        },
        clean: (state) => {
            state.request = undefined;
            state.selected = undefined;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveRoutine.pending, (state, action) => {
                state.request = action.meta.arg;
                state.state = loading();
                state.selected = undefined;
            })
            .addCase(retrieveRoutine.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.selected = action.payload;
            })
            .addCase(retrieveRoutine.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(imageSetAvatarThunk.fulfilled, (state, action) => {
                if (state.selected
                    && action.meta.arg.objectTarget == ObjectTarget.ROUTINE
                    && action.meta.arg.targetId == state.selected?.id
                ) {
                    state.selected.url = action.payload;
                }
            });
    },
});

export interface RoutineInfo {
    id: string;
    name: string;
    url?: string;
    schoolId: string;
}

// export const { } = routineSlice.actions;

export const selectRoutine = (state: RootState) => state.routine;

export default routineSlice.reducer;
